import * as React from 'react';
import {Pdf} from "../../pdf/Pdf";
import {BaseRouteProps} from '../../../interfaces';
import * as moment from 'moment';
import './pdf_user_guide.css';

interface PdfUserGuideProps extends Partial<BaseRouteProps<{}>> {
    userName?: string;
    organization?: string;
}

export class PdfUserGuide extends React.Component<PdfUserGuideProps> {
    public render() {
        const { userName, organization } = this.props;
        const date = moment().format('MMMM D, YYYY');
        
        return (
            <Pdf fontSize="12.75px">
                <Pdf.TitlePage 
                    title="PVA Style Report User’s Guide" 
                    subtitle="User’s Guide" 
                    name={userName}
                    date={date}
                    organization={organization}
                    currentPage={1}
                    totalPages={5}
                />
                
                <Pdf.Page>
                    <div className="user-guide-content">
                        <h2>Welcome to the Pondera® Virtual Advisor (PVA) Style User's Guide</h2>
                        <p>
                            In this document you will find information on the PVA, the PVA Styles, and how to utilize this information to
                            enhance your work productivity including communication, relationships, and more.
                        </p>
                        
                        <h3>INTRODUCTION TO THE PVA AND THE PVA STYLES:</h3>
                        
                        <p>
                            The Pondera® Virtual Advisor (PVA) is an online assessment tool, measuring personal preferences and
                            inclinations that helps individuals gain self-insight, learn to manage their strengths, and find their best fit
                            roles. The main objective of the PVA is to initiate an individual's self-awareness development process, which
                            includes the identification of their PVA Style and acceptance of their natural inclinations and strengths. It is
                            through this identification that a deeper journey of self-insight can begin. Armed with these insights,
                            individuals will be able to advocate for their needs, increase communication effectiveness and acknowledge
                            and manage their strengths so as to not over do them or fall prey to potential blind spots.
                        </p>
                        
                        <p>
                            There are eight possible PVA Styles; Maverick, Catalyst, Organizer, Relator, Pragmatist, Strategist, Analyzer, and
                            Evaluator. Individuals are best able to navigate the world of organizations and respond best to environmental
                            demands when they live authentically out of their most natural style and are able to utilize their style
                            appropriately.
                        </p>
                        
                        <p>
                            While we are all capable of utilizing aspects from all PVA Styles, the PVA Style identified by the instrument is
                            intended to be the one that is most natural for you.
                        </p>
                        
                        <p>
                            The purpose of the PVA is not to classify individuals into categories, but rather, similar to Carl Jung, provide
                            an explanation of differences among individuals (Jung, 554). The explanation of differences is an essential
                            means for determining the "personal equation" (Jung, 555) of an individual, who, armed with the knowledge
                            of his or her PVA Style, can avoid many serious blunders in communicating and dealing with others around
                            them as well as allowing them to more effectively advocate for what they need in order to be successful.
                        </p>
                        
                        <p>
                            The importance of the PVA Style cannot be overstated; it establishes the framework by which an individual
                            can best lead their life. However, the ability to manage this style and its strengths is equally important as any
                            strength over done can become a weakness and that living authentically and "in-balance" is key.
                        </p>
                        
                        <p>
                            It is important to note that one style is not "better" than another, yet some styles are better suited for
                            particular roles than others. Consequently, different styles require different environments for optimal
                            functioning, it is therefore useful, if not essential, to be aware of your style's natural inclinations.
                        </p>
                    </div>
                    <Pdf.Footer currentPage={2} totalPages={5} />
                </Pdf.Page>

                <Pdf.Page>
                    <div className="user-guide-content">
                        <h3>BREAKING DOWN YOUR PVA STYLE:</h3>
                        <p>Each of the eight PVA Style descriptions is broken down into the following sections:</p>
                        <ul>
                            <li>KEY DESCRIPTORS</li>
                            <li>KEY CHARACTERISTICS</li>
                            <li>AT THEIR BEST</li>
                            <li>OUT OF BALANCE</li>
                            <li>TEAM INTERACTION</li>
                            <li>WHAT THEY VALUE IN AND FROM MEETINGS</li>
                            <li>IDEAL TEAM FUNCTION OR ROLE</li>
                            <li>DEMOTIVATORS</li>
                            <li>KEYS TO REMEMBER</li>
                        </ul>
                        
                        <p>The following quick reference tabs are also provided:</p>
                        <ul>
                            <li>STRENGTHS</li>
                            <li>COMMUNICATION</li>
                            <li>IDEAL ENVIRONMENT</li>
                            <li>DEMOTIVATORS</li>
                            <li>SECONDARY STYLES</li>
                        </ul>
                        
                        <p>
                            <em>Note: All content written about the PVA Styles is intentionally written to cover a broad range for each style. 
                            Therefore, there may be statements that do not resonate in their entirety.</em>
                        </p>
                    </div>
                    <Pdf.Footer currentPage={3} totalPages={5} />
                </Pdf.Page>

                <Pdf.Page>
                    <div className="user-guide-content">
                        <h3>YOUR PVA STYLE:</h3>
                        <p>Each PVA Style begins with a brief 1-2 sentence description of the style as well as six key descriptors.</p>

                        <h3>Key Characteristics:</h3>
                        <p>In this section you will find a list of Key Characteristics that are common to this Style.</p>

                        <h3>At their best/Out of Balance:</h3>
                        <p>
                            In this section you will find descriptions of your PVA Style at its best and when out of balance. "At their best" 
                            describes how your Style can be experienced when lived authentically and in alignment. You do this by 
                            accepting yourself and the strengths of your style. "Out of Balance" is how your Style can be experienced 
                            when there is an over reliance and overdone expression of your Style. This can happen when you are 
                            generally overwhelmed, are under a lot of stress, or are not living in alignment with your Style.
                        </p>
                        <p>
                            We cite this as a way to raise awareness to the importance of maintaining psychic balance. Accessing the 
                            best of your style requires introverted styles to embrace and balance their extraverted nature and requires 
                            extraverted styles to embrace their introverted nature. This proper equilibrium helps individuals gain access 
                            to their best selves.
                        </p>

                        <h3>Team Interaction:</h3>
                        <p>
                            In this section you will find a list of common team dynamics when working on a team and with others. You 
                            will also find things that your Style may find irritating; while you may not act on this "irritation" that does not 
                            mean that you don't feel it or experience it. Same with others; you will find ways in which your Style may 
                            irritate others.
                        </p>

                        <h3>What They Value In and From Meetings:</h3>
                        <p>In this section you will find a list of bullet points detailing ways meetings can be more engaging and valuable for you.</p>

                        <h3>Ideal Team Function or Role:</h3>
                        <p>This section highlights some potential roles that may take advantage of the gifts of your Style. When utilized, they may make things more enjoyable.</p>

                        <h3>Demotivators:</h3>
                        <p>In this section you will find some common demotivators for your Style. When present, you may find your experiences to be less than ideal or at times discouraging.</p>

                        <h3>Keys to Remember:</h3>
                        <p>In this section you will find a general synopsis with key takeaways for your PVA Style.</p>
                    </div>
                    <Pdf.Footer currentPage={4} totalPages={5} />
                </Pdf.Page>

                <Pdf.Page>
                    <div className="user-guide-content">
                        <h3>QUICK REFERENCE TABS:</h3>
                        <p>We include this content as a quick reference for additional key information about your style.</p>

                        <h3>Strengths:</h3>
                        <p>
                            In this section you will find a list of general strengths of your PVA Style. These strengths can be used 
                            productively and holistically with sufficient self-awareness and emotional health. While it is important to be 
                            aware of your Style's strengths, it is equally important to be aware that a strength overdone has the potential 
                            to become a weakness or a blind spot.
                        </p>

                        <h3>Communication:</h3>
                        <p>In this section you will find a list of do's and don'ts that others should consider when communicating with you.</p>

                        <h3>Ideal Environment:</h3>
                        <p>
                            In this section you will find a general list of bullet points that describe an environment that your PVA Style 
                            may thrive or feel comfortable in.
                        </p>

                        <h3>Demotivators:</h3>
                        <p>
                            In this section you will find a list of common demotivators for your PVA Style. While all styles are unique, each 
                            style has certain activities that they find motivating and others that are demotivating. Note: these are the 
                            same demotivators seen in the summary of your PVA Style.
                        </p>

                        <h3>Secondary Styles:</h3>
                        <p>
                            In this section you will find descriptions of the differences between the two possible Secondary PVA Styles 
                            for your Primary PVA Style.
                        </p>

                        <div className="user-guide-footer-section">
                            <h3>THE PVA STYLES:</h3>
                            <p>
                                As you learn about the preferences of your style you can also learn about the preferences of the styles of 
                                your colleagues and those you interact with. For example, the Communication Quick Reference Tab can be a 
                                valuable resource when learning how to best work with different styles. Content on the other PVA Styles can 
                                be found by clicking on the PVA Styles tab on the navigation bar on the left side of your browser window.
                            </p>
                        </div>
                    </div>
                    <Pdf.Footer currentPage={5} totalPages={5} />
                </Pdf.Page>
            </Pdf>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/individual/pdf_user_guide.tsx